/**
 * Hook moderne pour la gestion des abonnements et tokens
 * Basé sur la documentation API backend mise à jour
 */

import { useState, useEffect, useCallback } from 'react';
import {
  SubscriptionPlan,
  CustomerTokenBalance,
  TokenTransaction,
  Subscription,
  CreateSubscriptionData,
  GetPlansParams,
  GetTokenTransactionsParams,
  CustomerType
} from '../types/subscription';

import modernSubscriptionService from '../services/modernSubscription';

export interface UseModernSubscriptionReturn {
  // State moderne
  subscription: Subscription | null;
  plans: SubscriptionPlan[];
  tokenBalance: CustomerTokenBalance | null;
  tokenTransactions: TokenTransaction[];
  isLoading: boolean;
  error: string | null;

  // Actions enrichies
  loadSubscription: () => Promise<void>;
  loadPlans: (params?: GetPlansParams) => Promise<void>;
  loadTokenBalance: () => Promise<void>;
  loadTokenTransactions: (params?: GetTokenTransactionsParams) => Promise<void>;
  createSubscription: (data: CreateSubscriptionData) => Promise<void>;
  
  // Utilitaires enrichis
  getPlansByType: (customerType: CustomerType) => SubscriptionPlan[];
  getMonthlyCost: (plan: SubscriptionPlan) => number;
  getAnnualCost: (plan: SubscriptionPlan) => number;
  getAnnualSavings: (plan: SubscriptionPlan) => number;
  formatTokenAmount: (amount: number) => string;
  getRemainingTokensPercentage: () => number;

  // === COMPATIBILITÉ UI EXISTANTE ===
  getReceiptData: () => ReceiptCompatData | null;
  getTokenRingData: () => TokenRingCompatData;
  getLegacySubscriptionData: () => LegacySubscriptionData | null;
  getLegacyPlansData: () => LegacyPlanData[];
  
  // === PROPRIÉTÉS ENRICHIES ===
  smePlans: SubscriptionPlan[];
  fiPlans: SubscriptionPlan[];
  getAnnualPrice: (plan: SubscriptionPlan) => number;
}

// Types de compatibilité pour les UI existantes
export interface ReceiptCompatData {
  payment: {
    id: string;
    date: string;
    amount: number;
    method: string;
    plan: string;
    status: string;
    receiptUrl?: string;
  };
}

export interface TokenRingCompatData {
  tokenBalance: number;
  tokenTotal: number;
}

export interface LegacySubscriptionData {
  id: string;
  userId: string;
  currentPlan: string;
  tokenBalance: number;
  tokenTotal: number;
  paymentHistory: any[];
}

export interface LegacyPlanData {
  name: string;
  description: string;
  price: number;
  features: string[];
  highlighted?: boolean;
  demo?: boolean;
  tokenAmount?: string;
}

export const useModernSubscription = (): UseModernSubscriptionReturn => {
  // State
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [tokenBalance, setTokenBalance] = useState<CustomerTokenBalance | null>(null);
  const [tokenTransactions, setTokenTransactions] = useState<TokenTransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load functions
  const loadSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await modernSubscriptionService.getCurrentSubscription();
      setSubscription(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async (params?: GetPlansParams) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await modernSubscriptionService.getSubscriptionPlans(params);
      setPlans(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadTokenBalance = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await modernSubscriptionService.getTokenBalance();
      setTokenBalance(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadTokenTransactions = useCallback(async (params?: GetTokenTransactionsParams) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await modernSubscriptionService.getTokenTransactions(params);
      setTokenTransactions(response.transactions);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Actions
  const createSubscription = useCallback(async (data: CreateSubscriptionData) => {
    try {
      setIsLoading(true);
      setError(null);
      const newSubscription = await modernSubscriptionService.createSubscription(data);
      setSubscription(newSubscription);
      // Recharger le solde de tokens car l'abonnement peut modifier l'allocation
      await loadTokenBalance();
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      throw error; // Re-throw pour permettre au composant de gérer l'erreur
    } finally {
      setIsLoading(false);
    }
  }, [loadTokenBalance]);

  // Utility functions
  const getPlansByType = useCallback((customerType: CustomerType): SubscriptionPlan[] => {
    return plans.filter(plan => plan.customerType === customerType);
  }, [plans]);

  const getMonthlyCost = useCallback((plan: SubscriptionPlan): number => {
    return plan.monthlyPriceUSD;
  }, []);

  const getAnnualCost = useCallback((plan: SubscriptionPlan): number => {
    return plan.annualPriceUSD;
  }, []);

  const getAnnualSavings = useCallback((plan: SubscriptionPlan): number => {
    const monthlyTotal = plan.monthlyPriceUSD * 12;
    return monthlyTotal - plan.annualPriceUSD;
  }, []);

  const formatTokenAmount = useCallback((amount: number): string => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    } else {
      return amount.toString();
    }
  }, []);

  const getRemainingTokensPercentage = useCallback((): number => {
    if (!tokenBalance || tokenBalance.totalTokens === 0) return 0;
    return (tokenBalance.remainingTokens / tokenBalance.totalTokens) * 100;
  }, [tokenBalance]);

  // === MÉTHODES DE COMPATIBILITÉ UI ===
  
  const getReceiptData = useCallback((): ReceiptCompatData | null => {
    if (!subscription) return null;

    return {
      payment: {
        id: subscription.id,
        date: subscription.currentPeriodStart,
        amount: subscription.plan.monthlyPriceUSD,
        method: subscription.paymentMethod.type === 'card' ? 'Carte bancaire' :
                subscription.paymentMethod.type === 'mobile' ? 'Mobile Money' : 'Paiement manuel',
        plan: subscription.plan.name,
        status: subscription.status === 'active' ? 'Payé' : 'En attente',
        receiptUrl: `#/receipt/${subscription.id}`
      }
    };
  }, [subscription]);

  const getTokenRingData = useCallback((): TokenRingCompatData => {
    if (!tokenBalance) return { tokenBalance: 0, tokenTotal: 1 };

    return {
      tokenBalance: tokenBalance.remainingTokens,
      tokenTotal: tokenBalance.totalTokens
    };
  }, [tokenBalance]);

  const getLegacySubscriptionData = useCallback((): LegacySubscriptionData | null => {
    if (!subscription || !tokenBalance) return null;

    return {
      id: subscription.id,
      userId: subscription.customerId,
      currentPlan: subscription.plan.name,
      tokenBalance: tokenBalance.remainingTokens,
      tokenTotal: tokenBalance.totalTokens,
      paymentHistory: [] // À implémenter avec les transactions si nécessaire
    };
  }, [subscription, tokenBalance]);

  const getLegacyPlansData = useCallback((): LegacyPlanData[] => {
    return plans.map(plan => ({
      name: plan.name,
      description: plan.description,
      price: plan.monthlyPriceUSD,
      features: Object.values(plan.features).map(f => f.description).filter((desc): desc is string => Boolean(desc)),
      highlighted: plan.isPopular,
      demo: plan.monthlyPriceUSD === 0,
      tokenAmount: formatTokenAmount(plan.tokenAllocation.monthlyTokens)
    }));
  }, [plans, formatTokenAmount]);

  // Initial load
  useEffect(() => {
    const initialLoad = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Charger les données essentielles en parallèle
        await Promise.all([
          loadSubscription(),
          loadPlans(),
          loadTokenBalance()
        ]);
      } catch (err) {
        const error = err as Error;
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    initialLoad();
  }, [loadSubscription, loadPlans, loadTokenBalance]);

  return {
    // State
    subscription,
    plans,
    tokenBalance,
    tokenTransactions,
    isLoading,
    error,

    // Actions
    loadSubscription,
    loadPlans,
    loadTokenBalance,
    loadTokenTransactions,
    createSubscription,

    // Utilities
    getPlansByType,
    getMonthlyCost,
    getAnnualCost,
    getAnnualSavings,
    formatTokenAmount,
    getRemainingTokensPercentage,

    // Compatibilité UI
    getReceiptData,
    getTokenRingData,
    getLegacySubscriptionData,
    getLegacyPlansData,

    // Propriétés enrichies
    smePlans: plans.filter(p => p.customerType === CustomerType.SME),
    fiPlans: plans.filter(p => p.customerType === CustomerType.FINANCIAL_INSTITUTION),
    getAnnualPrice: getAnnualCost
  };
};

// Hook spécialisé pour les plans SME
export const useSMEPlans = () => {
  const { plans, loadPlans, isLoading, error } = useModernSubscription();
  
  const smePlans = plans.filter(plan => plan.customerType === CustomerType.SME);
  
  useEffect(() => {
    loadPlans({ customerType: CustomerType.SME });
  }, [loadPlans]);

  return {
    plans: smePlans,
    isLoading,
    error,
    refresh: () => loadPlans({ customerType: CustomerType.SME })
  };
};

// Hook spécialisé pour les plans Institution Financière
export const useFinancialInstitutionPlans = () => {
  const { plans, loadPlans, isLoading, error } = useModernSubscription();
  
  const fiPlans = plans.filter(plan => plan.customerType === CustomerType.FINANCIAL_INSTITUTION);
  
  useEffect(() => {
    loadPlans({ customerType: CustomerType.FINANCIAL_INSTITUTION });
  }, [loadPlans]);

  return {
    plans: fiPlans,
    isLoading,
    error,
    refresh: () => loadPlans({ customerType: CustomerType.FINANCIAL_INSTITUTION })
  };
};

// Hook pour la comparaison de plans
export const usePlanComparison = (planIds: string[]) => {
  const { plans } = useModernSubscription();
  
  const comparisonPlans = plans.filter(plan => planIds.includes(plan.id));
  
  const compareFeatures = useCallback(() => {
    if (comparisonPlans.length === 0) return {};
    
    // Créer un objet avec toutes les fonctionnalités uniques
    const allFeatures = new Set<string>();
    comparisonPlans.forEach(plan => {
      Object.keys(plan.features).forEach(feature => allFeatures.add(feature));
    });
    
    // Créer la matrice de comparaison
    const comparison: Record<string, Record<string, any>> = {};
    Array.from(allFeatures).forEach(feature => {
      comparison[feature] = {};
      comparisonPlans.forEach(plan => {
        comparison[feature][plan.id] = plan.features[feature] || { enabled: false };
      });
    });
    
    return comparison;
  }, [comparisonPlans]);

  return {
    plans: comparisonPlans,
    compareFeatures
  };
};
